/**
 * Utility for formatting large numbers in compact form.
 * Reusable for MoneyDisplay and future chart components.
 */

// Thresholds starting at 10 million
const COMPACT_THRESHOLDS = [
    { value: 1_000_000_000, suffix: 'Bil' },
    { value: 10_000_000, suffix: 'Mil' },
];

interface CompactNumberResult {
    /** The divided number (e.g., 12.35 for 12,350,000) */
    number: number;
    /** The suffix to display (e.g., 'Mil' or 'Bil') */
    suffix: string;
}

/**
 * Converts a large number to compact form if it meets the threshold.
 * @param value - The number to potentially compact
 * @returns Object with divided number and suffix, or null if below threshold
 *
 * @example
 * formatCompactNumber(12_345_678) // { number: 1.2345678, suffix: 'Mil' }
 * formatCompactNumber(1_234_567_890) // { number: 1.23456789, suffix: 'Bil' }
 * formatCompactNumber(9_999_999) // null (below 10M threshold)
 */
export function formatCompactNumber(value: number): CompactNumberResult | null {
    const absValue = Math.abs(value);

    for (const { value: threshold, suffix } of COMPACT_THRESHOLDS) {
        if (absValue >= threshold) {
            return {
                number: value / threshold,
                suffix,
            };
        }
    }

    return null;
}
